
import { Component, Vue } from "vue-property-decorator";

import Preloader from "@/components/Preloader.vue";
import CPaginatedSelect from "@/components/PaginatedSelect.vue";
import Search from "@/components/Search.vue";
import CStructure, { IStructureProp } from "@/components/Structure.vue";
import CDelegate from "@/views/team/components/Delegate.vue";
import CWorkplacePlaceholder from "@/components/WorkplacePlaceholder.vue";
import CInfiniteScroll from "@/components/InfiniteScroll.vue";

import { EDeviceTypes } from "@/enums/deviceTypes";

import user_routes from "@/api/routes/users";
import akt_routes from "@/api/routes/akt";

import PositionLocal from "@/models/position/local";
import { IAktTemplate } from "@/models/akt/template";
import { IAktWorkplaceGroup } from "@/models/akt/assignment";
import StructureFactory from "@/models/structure/factory";
import structureRoutes from "@/api/routes/structures";
import { IRouteMeta } from "@/router/interface";
import { EAdmissionPermission } from "@/models/admission/permission";
import { getState, setState, delState } from "@/helpers/local_storage";

interface IData {
  readonly current_page: number;
  readonly next_page: boolean;
}

interface IDataPosition {
  readonly id: string | null;
  readonly name: string;
}

interface IDataProgress {
  readonly id: number | null;
  readonly name: string;
  readonly clazz: string;
}

interface ITeammate {
  readonly id: string;
  readonly full_name: string;
  readonly identification_number: string;
  readonly email?: string;
  readonly login?: string;
  readonly progress_status: number;
  readonly position_name?: string;
}

interface IDataTeammate extends IData {
  readonly teammates: ITeammate[];
}

interface IDataTemplates extends IData {
  readonly templates: IAktTemplate[];
}

@Component({
  components: {
    Preloader,
    CPaginatedSelect,
    Search,
    CDelegate,
    CStructure,
    CWorkplacePlaceholder,
    CInfiniteScroll
  },
  data: () => {
    return {
      EDeviceTypes
    };
  }
})
export default class AllTeam extends Vue {
  public preload: boolean = false;
  public preload_infinite: boolean = false;

  private filter_key_prefix = "all_team_";

  protected per_page: number = 100;
  protected current_page: number = 0;
  protected next_page: boolean = false;
  public teammates: ITeammate[] = [];

  public workplace_exist: boolean = true;
  public show_akt: boolean = true;

  public direct_subordinates_only: boolean = true;
  private direct_subordinates_only_key = `${this.filter_key_prefix}_direct_subordinates_only`;

  public show_filter: boolean = false;
  public filters_count: number = 0;
  public filters: Map<string, boolean> = new Map();

  private search: string = this.$store.state.search;
  private current_user: IJWTUser | null = this.$store.state.currentUser;

  public position_id: string | null = null;
  private position_id_key = `${this.filter_key_prefix}_position_id`;

  public factories: StructureFactory[] = [
    { id: null, full_name: "Все", level_name: "Уровень" }
  ];

  public factory_id: string | null = null;
  private factory_id_key = `${this.filter_key_prefix}_factory_id`;

  public structure: IStructureProp = {};
  private structure_key = `${this.filter_key_prefix}_structure`;

  public progress: IDataProgress[] = [
    { id: null, name: "Все", clazz: "font-blue" },
    { id: 2, name: "Немного отстает", clazz: "font-orange" },
    { id: 3, name: "Сильно отстает по активным программам", clazz: "font-red" },
    { id: 4, name: "Успевает", clazz: "font-blue" }
  ];
  public progress_id: number | null = this.progress[0].id;
  private progress_id_key = `${this.filter_key_prefix}_progress_id`;

  public templates: IAktTemplate[] = [];
  public workplaces: IAktWorkplaceGroup[] = [];
  public akt_group_errors: string | null = null;

  private reload_positions_fn: Function | null = null;
  private watchers: Function[] = [];

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.preload = true;

    this.show_akt = this.hasAktPermissions() && this.allowWrite();

    this.watchers.push(
      this.$store.watch(
        state => state.search,
        async search => {
          this.search = search;

          await this.clearTeammates();
        }
      )
    );

    this.watchers.push(
      this.$store.watch(
        state => state.currentUser,
        currentUser => {
          this.current_user = currentUser;
        }
      )
    );

    await this.workplaceExist();

    this.preload = false;

    if (this.workplace_exist) {
      this.getLocalStorageState();

      const promises: Promise<void>[] = [];

      promises.push(this.loadFactories());
      promises.push(this.loadTeammates());

      if (this.show_akt) {
        promises.push(this.loadTemplates());
        promises.push(this.loadWorkplaces());
      }

      await Promise.all(promises);

      if (this.reload_positions_fn) {
        this.reload_positions_fn.call(null);
      }
    }
  }

  public async infiniteHandler() {
    if (this.next_page && !this.preload_infinite) {
      await this.loadTeammates(this.current_page + 1);
    }
  }

  private async loadTeammates(page: number = 0) {
    this.preload_infinite = true;

    const params = Object({
      factory_id: this.factory_id,
      infinity_id: this.structure.infinity_id,
      direct_subordinates_only: this.direct_subordinates_only,
      page,
      progress_id: this.progress_id,
      position: this.position_id,
      search: this.search,
      per_page: this.per_page,
      paginate: true
    });

    return this.$api
      .get(user_routes.team, { params })
      .then(({ data: res }: { data: IDataTeammate }) => {
        this.current_page = res.current_page;
        this.next_page = res.next_page;

        this.teammates.push(...res.teammates);
      })
      .finally(() => {
        this.preload_infinite = false;
      });
  }

  private async clearTeammates() {
    this.current_page = 0;
    this.teammates = [];

    await this.loadTeammates();
  }

  private async loadFactories() {
    return this.$api
      .get(structureRoutes.factories, {
        params: {
          only_my: true,
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: StructureFactory[] }) => {
        this.factories.push(...res);
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      });
  }

  public setReloadPositions(reload_fn: Function) {
    this.reload_positions_fn = reload_fn;
  }

  public async loadPositionFn(page: number = 0, search_query: string) {
    return this.$api
      .get(user_routes.position, {
        params: {
          page,
          per_page: 50,
          search: search_query,
          factory_id: this.factory_id,
          direct_subordinates_only: this.direct_subordinates_only,
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: PositionLocal[] }) => {
        return res.map(r => ({ name: r.name, id: r.id ?? null }));
      });
  }

  public async changePositionIdFilter(position_id: string | null) {
    this.position_id = position_id;

    if (this.position_id) {
      this.filters.set("position", true);
    } else {
      this.filters.delete("position");
    }

    setState(this.position_id_key, this.position_id);

    this.filters_count = this.filters.size;

    await this.clearTeammates();
  }

  public async changeStructureFilter(structure: IStructureProp) {
    this.structure = structure;

    setState(this.structure_key, this.structure);

    await this.clearTeammates();
  }

  public async changeFactoryFilter() {
    if (this.factory_id) {
      this.filters.set("factory", true);
    } else {
      this.filters.delete("factory");
    }

    setState(this.factory_id_key, this.factory_id);

    this.structure = {};
    delState(this.structure_key);

    this.position_id = null;
    this.filters.delete("position");
    delState(this.position_id_key);

    this.filters_count = this.filters.size;

    if (this.reload_positions_fn) {
      this.reload_positions_fn.call(null);
    }

    await this.clearTeammates();
  }

  public async changeProgressFilter() {
    if (this.progress_id) {
      this.filters.set("progress", true);
    } else {
      this.filters.delete("progress");
    }

    setState(this.progress_id_key, this.progress_id);

    this.filters_count = this.filters.size;

    await this.clearTeammates();
  }

  public async changeDirectSubordinatesOnlyFilter() {
    if (this.direct_subordinates_only) {
      this.filters.set("direct_subordinates_only", true);
    } else {
      this.filters.delete("direct_subordinates_only");
    }

    setState(this.direct_subordinates_only_key, this.direct_subordinates_only);

    this.filters_count = this.filters.size;

    if (this.reload_positions_fn) {
      this.reload_positions_fn.call(null);
    }

    await this.clearTeammates();
  }

  public async clearFilters() {
    this.filters.clear();

    this.position_id = null;
    delState(this.position_id_key);

    this.progress_id = null;
    delState(this.progress_id_key);

    this.factory_id = null;
    delState(this.factory_id_key);
    delState(this.structure_key);

    this.direct_subordinates_only = true;
    this.filters.set("direct_subordinates_only", true);
    setState(this.direct_subordinates_only_key, this.direct_subordinates_only);

    this.filters_count = this.filters.size;

    if (this.reload_positions_fn) {
      this.reload_positions_fn.call(null);
    }

    await this.clearTeammates();
  }

  private async workplaceExist() {
    return this.$api
      .get(user_routes.workplace_exists(this.current_user!.id))
      .then(({ data: res }: { data: { is_exist: boolean } }) => {
        this.workplace_exist = res.is_exist;
      });
  }

  public async loadXlsxTeam() {
    const params = Object({
      factory_id: this.factory_id,
      infinity_id: this.structure.infinity_id,
      workline_id: this.structure.workline_id,
      workshop_id: this.structure.workshop_id,
      equipment_id: this.structure.equipment_id,
      direct_subordinates_only: this.direct_subordinates_only,
      progress_id: this.progress_id,
      position: this.position_id
    });

    return this.$api
      .post(user_routes.post_team_xlsx, params)
      .then(({ data: res }: { data: { base64: string } }) => {
        const link = document.createElement("a");
        link.download = "Моя команда.xlsx";
        link.href = res.base64;
        link.click();
        link.remove();
      });
  }

  private async loadWorkplaces() {
    return this.$api
      .get(akt_routes.groups, {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: IAktWorkplaceGroup[] }) => {
        this.workplaces = res;
      });
  }

  private async loadTemplates() {
    return this.$api
      .get(akt_routes.templates_actual, {
        params: {
          paginate: false,
          include_archived: false
        }
      })
      .then(({ data: res }: { data: IDataTemplates }) => {
        this.templates = res.templates;
      });
  }

  public showAktGroupModal() {
    if (!this.show_akt) {
      return;
    }

    this.$modal.show("aktGroup");
  }

  public hideAktGroupModal() {
    this.$modal.hide("aktGroup");
  }

  public async saveAktGroups() {
    if (!this.show_akt) {
      return;
    }

    return this.$api
      .post(akt_routes.groups, { workplaces: this.workplaces })
      .then(() => {
        this.hideAktGroupModal();
      })
      .catch(({ response: res }) => {
        this.akt_group_errors = res.data.error;
      });
  }

  public getProgressById(id: number) {
    return this.progress.find(i => i.id === (id ?? 4));
  }

  public allowWrite() {
    return this.$api.allowWrite();
  }

  private hasAktPermissions() {
    return this.$api.hasWritePermissions([
      EAdmissionPermission.AKT_COMPLETED,
      EAdmissionPermission.AKT_SETTINGS,
      EAdmissionPermission.AKT_TEMPLATES
    ]);
  }

  private getLocalStorageState() {
    const progress_id = getState<number | null>(this.progress_id_key);
    if (progress_id?.value) {
      this.progress_id = progress_id.value;

      if (this.progress_id) {
        this.filters.set("progress", true);
      }
    }

    const direct_subordinates_only = getState<boolean>(
      this.direct_subordinates_only_key
    );
    if (direct_subordinates_only?.value != null) {
      this.direct_subordinates_only = direct_subordinates_only.value;

      if (this.direct_subordinates_only) {
        this.filters.set("direct_subordinates_only", true);
      }
    }

    const factory_id = getState<string | null>(this.factory_id_key);
    if (factory_id?.value) {
      this.factory_id = factory_id.value;

      if (this.factory_id) {
        this.filters.set("factory", true);
      }
    }

    const position_id = getState<string | null>(this.position_id_key);
    if (position_id?.value) {
      this.position_id = position_id.value;

      if (this.position_id) {
        this.filters.set("position", true);
      }
    }

    const structure = getState<IStructureProp | null>(this.structure_key);
    if (structure?.value) {
      this.structure = structure.value;
    }

    this.filters_count = this.filters.size;
  }
}
